import { useState, useRef } from 'react';

/**
 * Shared primitives for inline-editable data tables, bulk status changes, and
 * lookup-driven status pill colors. Used by Programs (list), Components, Goals,
 * Invoices. Purely presentational — callers own data + persistence.
 *
 * Exports: pillColor(), StatusPill, EditableCell, SelectCheckbox, BulkStatusBar,
 * ColorSwatchPicker, STANDARD_COLORS. The row-selection hook lives separately in
 * src/hooks/useBulkSelect.js; the search dropdown is src/components/SearchableSelect.js.
 */

// Resolve a pill color: lookup value's own color → page fallback → neutral.
// `fallback` may be a single hex string or a { value: hex } map.
export function pillColor(value, lookups, fallback) {
  if (lookups) {
    const m = lookups.find(l => l.value === value);
    if (m && m.color) return m.color;
  }
  if (typeof fallback === 'string') return fallback;
  if (fallback && fallback[value]) return fallback[value];
  return '#95a5a6';
}

// Status pill whose color comes from lookup values (with a page fallback).
export function StatusPill({ value, lookups, fallback }) {
  if (!value) return <span style={{ color: '#ccc' }}>—</span>;
  const bg = pillColor(value, lookups, fallback);
  return (
    <span style={{ background: bg, color: 'white', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}>
      {value}
    </span>
  );
}

// Click-to-edit cell (Airtable/Sheets style). Renders an inline trigger that
// swaps to an input/select on click. Enter or blur saves, Esc cancels; selects
// save immediately on choosing an option.
// `onSave(rawStringValue)` should return a promise; the caller converts/persists.
export function EditableCell({
  value, type = 'text', options, onSave, display,
  placeholder, step, align = 'left', emptyText = '—', disabled, rows = 4,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const skipBlur = useRef(false);  // guards double-commit from Enter+blur / Esc

  const begin = () => {
    if (disabled || saving) return;
    skipBlur.current = false;
    setDraft(value == null ? '' : String(value));
    setEditing(true);
  };

  const cancel = () => {
    skipBlur.current = true;
    setEditing(false);
  };

  // `override` lets selects commit the chosen value immediately (before state settles).
  const commit = async (override) => {
    if (skipBlur.current) return;
    skipBlur.current = true;
    const raw = override !== undefined ? override : draft;
    const orig = value == null ? '' : String(value);
    if (raw === orig) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(raw);
      setEditing(false);
    } catch (err) {
      alert('Save failed: ' + (err?.response?.data?.detail || err?.message || 'unknown error'));
      skipBlur.current = false;  // let them retry
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    const sharedStyle = {
      width: '100%', boxSizing: 'border-box', padding: '0 6px', height: '26px',
      fontSize: '13px', border: '1px solid #3498db', borderRadius: '3px',
      verticalAlign: 'middle', margin: 0,
      ...(type === 'date' ? { minWidth: '140px' } : {}),
    };
    const onKey = e => {
      if (e.key === 'Enter') commit();
      else if (e.key === 'Escape') cancel();
    };
    if (type === 'select') {
      // Selecting an option saves immediately — no need to click away.
      // onBlur is deferred a tick rather than cancelling synchronously —
      // some browsers fire blur before (or interleaved with) the change
      // event when an option is chosen via mouse, and a synchronous cancel()
      // there would set skipBlur before commit() ever runs, silently
      // discarding the selection (looked like "inline editing does nothing"
      // from the outside — the click registered, the value even updated in
      // the DOM, but the save never fired). The deferred check re-reads
      // skipBlur.current after commit() has had a chance to set it first.
      return (
        <select autoFocus value={draft} disabled={saving} style={sharedStyle}
          onChange={e => { setDraft(e.target.value); commit(e.target.value); }}
          onBlur={() => setTimeout(() => { if (!skipBlur.current) cancel(); }, 150)}
          onKeyDown={onKey}>
          {(options || []).map(o => (
            <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
          ))}
        </select>
      );
    }
    if (type === 'textarea') {
      // Enter has to insert a newline here rather than commit (the whole point of a
      // multi-line field), so this is the one type that can't use `onKey` — blur
      // saves, Esc cancels, and Cmd/Ctrl+Enter saves without reaching for the mouse.
      return (
        <textarea autoFocus value={draft} disabled={saving} rows={rows}
          placeholder={placeholder}
          style={{
            ...sharedStyle, height: 'auto', padding: '6px 8px',
            lineHeight: '1.5', resize: 'vertical', fontFamily: 'inherit',
          }}
          onChange={e => setDraft(e.target.value)}
          onBlur={() => commit()}
          onKeyDown={e => {
            if (e.key === 'Escape') cancel();
            else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit();
          }} />
      );
    }
    return (
      <input autoFocus value={draft} disabled={saving} style={sharedStyle}
        type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
        step={step} placeholder={placeholder}
        onChange={e => setDraft(e.target.value)} onBlur={() => commit()} onKeyDown={onKey} />
    );
  }

  const empty = value === '' || value == null;
  const shown = display != null ? display : (empty ? <span style={{ color: '#ccc' }}>{emptyText}</span> : value);
  const justify = align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start';
  return (
    <span
      onClick={begin}
      title={disabled ? undefined : 'Click to edit'}
      style={{
        display: 'inline-flex', justifyContent: justify,
        // A textarea's value keeps its line breaks, so the read-only trigger has to
        // preserve them too (and top-align) rather than collapsing to one centered line.
        alignItems: type === 'textarea' ? 'flex-start' : 'center',
        ...(type === 'textarea' ? { whiteSpace: 'pre-wrap' } : {}),
        width: '100%', boxSizing: 'border-box', minHeight: '26px',
        cursor: disabled ? 'default' : 'text',
        opacity: saving ? 0.5 : 1, borderRadius: '3px', padding: '0 3px',
        borderBottom: disabled ? '1px solid transparent' : '1px dotted #dde3e9',
      }}
      onMouseEnter={e => {
        if (disabled) return;
        e.currentTarget.style.background = '#eef6ff';
        e.currentTarget.style.borderBottom = '1px solid #3498db';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.borderBottom = disabled ? '1px solid transparent' : '1px dotted #dde3e9';
      }}
    >
      {shown}
    </span>
  );
}

// Row-select checkbox (stops propagation so it doesn't trigger row clicks).
export function SelectCheckbox({ checked, onChange }) {
  return (
    <input type="checkbox" checked={checked} onChange={onChange}
      onClick={e => e.stopPropagation()} style={{ cursor: 'pointer' }} />
  );
}

// Bulk action bar — appears above a table when rows are selected. Lets the user
// pick a status and apply it to all selected rows. `onApply(statusValue)`.
export function BulkStatusBar({ count, statuses, onApply, onClear }) {
  const [val, setVal] = useState('');
  const [applying, setApplying] = useState(false);
  if (!count) return null;
  const apply = async () => {
    if (!val) return;
    setApplying(true);
    try { await onApply(val); setVal(''); }
    finally { setApplying(false); }
  };
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', background: '#eaf2fb', border: '1px solid #b8d4f0', borderRadius: '6px', padding: '10px 16px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>{count} selected</span>
        <span style={{ fontSize: '13px', color: '#555' }}>Set status:</span>
        <select value={val} onChange={e => setVal(e.target.value)}
          style={{ padding: '6px 8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '13px' }}>
          <option value="">Choose…</option>
          {statuses.map(s => <option key={s.id} value={s.value}>{s.value}</option>)}
        </select>
        <button onClick={apply} disabled={!val || applying}
          style={{ padding: '6px 14px', background: (val && !applying) ? '#27ae60' : '#ccc', color: 'white', border: 'none', borderRadius: '4px', cursor: (val && !applying) ? 'pointer' : 'default', fontSize: '13px' }}>
          {applying ? 'Applying…' : 'Apply'}
        </button>
        <button onClick={onClear}
          style={{ padding: '6px 12px', background: 'none', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', color: '#555' }}>
          Clear
        </button>
      </div>
    </div>
  );
}

// Standard app palette for editable pill colors (Admin → Lookup Values).
export const STANDARD_COLORS = [
  { label: 'Green',    hex: '#27ae60' },
  { label: 'Teal',     hex: '#16a085' },
  { label: 'Blue',     hex: '#3498db' },
  { label: 'Purple',   hex: '#9b59b6' },
  { label: 'Yellow',   hex: '#f39c12' },
  { label: 'Orange',   hex: '#e67e22' },
  { label: 'Red',      hex: '#e74c3c' },
  { label: 'Dark Red', hex: '#c0392b' },
  { label: 'Grey',     hex: '#95a5a6' },
  { label: 'Dark',     hex: '#2c3e50' },
];

// Swatch grid for picking a standard color. `allowNone` adds a "default" swatch
// that sets the value to null (falls back to the app default pill color).
export function ColorSwatchPicker({ value, onChange, allowNone }) {
  const norm = (value || '').toLowerCase();
  const sw = (selected) => ({
    width: '24px', height: '24px', borderRadius: '4px', cursor: 'pointer',
    border: selected ? '2px solid #2c3e50' : '1px solid #ccc',
    boxShadow: selected ? '0 0 0 2px #d6eaf8' : 'none', padding: 0,
  });
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
      {STANDARD_COLORS.map(c => (
        <button key={c.hex} type="button" title={c.label} onClick={() => onChange(c.hex)}
          style={{ ...sw(norm === c.hex.toLowerCase()), background: c.hex }} />
      ))}
      {allowNone && (
        <button type="button" title="Default (no color)" onClick={() => onChange(null)}
          style={{ ...sw(!value), background: '#fff', color: '#888', fontSize: '11px', lineHeight: '20px' }}>
          ✕
        </button>
      )}
    </div>
  );
}
